/**
 * Firebase client — supports THREE sign-in providers:
 *   • Phone Auth (SMS OTP + invisible reCAPTCHA)
 *   • Email Link (passwordless magic link)
 *   • Google Sign-in (OAuth popup)
 *
 * All three yield a Firebase ID token that our backend exchanges for a
 * Social Pulse JWT via `POST /api/auth/firebase/session`.
 *
 * Env vars (all REACT_APP_ prefix, set in /app/frontend/.env):
 *   REACT_APP_FIREBASE_API_KEY
 *   REACT_APP_FIREBASE_AUTH_DOMAIN
 *   REACT_APP_FIREBASE_PROJECT_ID
 *   REACT_APP_FIREBASE_STORAGE_BUCKET
 *   REACT_APP_FIREBASE_MESSAGING_SENDER_ID
 *   REACT_APP_FIREBASE_APP_ID
 */
import { initializeApp } from "firebase/app";
import {
  getAuth,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  signOut as fbSignOut,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink as fbSignInWithEmailLink,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  linkWithPopup,
  linkWithPhoneNumber as fbLinkWithPhoneNumber,
  linkWithCredential,
  EmailAuthProvider,
  unlink as fbUnlink,
  reauthenticateWithPopup,
  reload as fbReloadUser,
} from "firebase/auth";

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

export const firebaseApp = initializeApp(firebaseConfig);
export const firebaseAuth = getAuth(firebaseApp);

// -------------------- Phone (SMS OTP) --------------------

/**
 * Lazily create an invisible reCAPTCHA verifier bound to a container id.
 * Firebase Phone Auth on web requires this — it's an unavoidable Google UX.
 */
export function ensureRecaptcha(containerId = "firebase-recaptcha") {
  if (typeof window === "undefined") return null;
  if (window.__spRecaptchaVerifier) return window.__spRecaptchaVerifier;
  const verifier = new RecaptchaVerifier(firebaseAuth, containerId, {
    size: "invisible",
    // Firebase requires this callback signature even though we don't use the token.
    // eslint-disable-next-line no-unused-vars
    callback: (_token) => { /* solved silently */ },
  });
  window.__spRecaptchaVerifier = verifier;
  return verifier;
}

export function clearRecaptcha() {
  try {
    window.__spRecaptchaVerifier?.clear();
  } catch { /* ignore */ }
  window.__spRecaptchaVerifier = null;
}

/**
 * Send an SMS OTP to a phone in E.164 format. Returns the confirmationResult
 * whose `.confirm(code)` method verifies the OTP and yields a Firebase user.
 *
 * We always clear + recreate the reCAPTCHA verifier so resends work reliably
 * (Firebase's verifier can go stale after a successful send/expired code).
 */
export async function sendPhoneOtp(phoneE164) {
  clearRecaptcha();
  const verifier = ensureRecaptcha("firebase-recaptcha");
  return await signInWithPhoneNumber(firebaseAuth, phoneE164, verifier);
}

// -------------------- Email Link (Passwordless) --------------------

const EMAIL_LINK_STORAGE_KEY = "sp_email_for_signin";

/**
 * Send a passwordless magic-link to the user's email.
 * On click, Firebase returns the user to `${origin}/login?...oobCode=...`
 * where we complete the sign-in.
 */
export async function sendEmailMagicLink(email) {
  const cleaned = (email || "").trim().toLowerCase();
  const actionCodeSettings = {
    url: `${window.location.origin}/login?email-link=1`,
    handleCodeInApp: true,
  };
  await sendSignInLinkToEmail(firebaseAuth, cleaned, actionCodeSettings);
  // Firebase requires storing the email locally so the return page can
  // verify without prompting again (prevents session hijack).
  try { window.localStorage.setItem(EMAIL_LINK_STORAGE_KEY, cleaned); } catch { /* ignore */ }
}

export function isEmailLinkCallback(href = window.location.href) {
  return isSignInWithEmailLink(firebaseAuth, href);
}

export function readStoredEmailForLink() {
  try { return window.localStorage.getItem(EMAIL_LINK_STORAGE_KEY); } catch { return null; }
}

/**
 * Complete the email-link sign-in. Accepts the email in case the user opened
 * the link on a different device from where they requested it.
 */
export async function completeEmailLinkSignIn(email, href = window.location.href) {
  const cleaned = (email || "").trim().toLowerCase();
  const cred = await fbSignInWithEmailLink(firebaseAuth, cleaned, href);
  try { window.localStorage.removeItem(EMAIL_LINK_STORAGE_KEY); } catch { /* ignore */ }
  return cred;
}

// -------------------- Google (OAuth popup) --------------------

const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({ prompt: "select_account" });

// Redirect-based sign-in is the DEFAULT.
//
// Emergent preview hosts (and any host that sets
// `Cross-Origin-Opener-Policy: same-origin`) prevent Firebase's
// signInWithPopup from detecting the popup close, so its promise
// hangs forever and the visitor sees "Signing in…" indefinitely.
// The redirect flow does a full-page navigation and has no such
// dependency, matching Firebase's own reliability recommendation.
// Ref: https://firebase.google.com/docs/auth/web/redirect-best-practices
export async function signInWithGoogleRedirect() {
  return await signInWithRedirect(firebaseAuth, googleProvider);
}

// Popup-based sign-in — kept as an explicit fallback for browsers
// where redirect isn't ideal (Safari iOS occasionally needs popup).
export async function signInWithGooglePopup() {
  return await signInWithPopup(firebaseAuth, googleProvider);
}

// Legacy name — DEPRECATED. Preserved so existing callers don't
// break during rollout; new code should call signInWithGoogleRedirect
// / signInWithGooglePopup explicitly. Behaves like a redirect (never
// resolves in the calling tab — the app receives the credential
// after reload via `readGoogleRedirectResult()`).
export async function signInWithGoogle() {
  return await signInWithGoogleRedirect();
}

// Read the credential returned by a completed Google redirect.
// Returns `null` on ordinary app loads (nothing to consume).
export async function readGoogleRedirectResult() {
  try {
    return await getRedirectResult(firebaseAuth);
  } catch (err) {
    // Re-throw so the AuthContext can surface a user-friendly banner.
    throw err;
  }
}

// -------------------- Sign out --------------------

/** Fully sign out of Firebase — clears all tokens from device storage. */
export async function firebaseSignOut() {
  clearRecaptcha();
  try { await fbSignOut(firebaseAuth); } catch { /* ignore */ }
}

// -------------------- Account Linking --------------------
//
// Firebase lets a single user attach MULTIPLE sign-in credentials so the
// same person can log in via any of Google / Phone / Email link and land
// on the same account. Below are the linking primitives used by the
// Profile page's "Sign-in methods" section.
//
// All functions assume `firebaseAuth.currentUser` is populated (the user
// must be signed in already). They throw the raw Firebase error so the
// caller can pattern-match on `err.code` (e.g. `auth/credential-already-in-use`).

/** Map Firebase providerId → human label for our UI. */
export const PROVIDER_LABELS = {
  "google.com": "Google",
  "phone":      "Phone",
  "password":   "Email link", // Firebase stores email-link credentials under "password"
};

/** List currently-linked providers on the signed-in Firebase user.
 * Returns an array of {providerId, email?, phoneNumber?, displayName?}.
 */
export function listLinkedProviders() {
  const u = firebaseAuth.currentUser;
  if (!u) return [];
  return (u.providerData || []).map((p) => ({
    providerId: p.providerId,
    email: p.email || null,
    phoneNumber: p.phoneNumber || null,
    displayName: p.displayName || null,
  }));
}

/** Reload the Firebase user object from the server (used after link/unlink). */
export async function reloadFirebaseUser() {
  const u = firebaseAuth.currentUser;
  if (!u) return;
  try { await fbReloadUser(u); } catch { /* ignore */ }
}

// ---- Link Google (OAuth popup) ----------------------------------------
export async function linkGoogleProvider() {
  const u = firebaseAuth.currentUser;
  if (!u) throw new Error("Not signed in");
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: "select_account" });
  const result = await linkWithPopup(u, provider);
  await reloadFirebaseUser();
  return result;
}

// ---- Link Phone (SMS OTP) ---------------------------------------------
/**
 * Start linking a phone number. Returns a `confirmationResult` whose
 * `.confirm(otp)` method finalises the link once the user enters the code.
 * Uses the same invisible reCAPTCHA verifier as first-time phone sign-in.
 */
export async function startLinkPhone(phoneE164) {
  const u = firebaseAuth.currentUser;
  if (!u) throw new Error("Not signed in");
  clearRecaptcha();
  const verifier = ensureRecaptcha("firebase-recaptcha");
  return await fbLinkWithPhoneNumber(u, phoneE164, verifier);
}

/** Confirm the phone-linking OTP. */
export async function confirmLinkPhone(confirmationResult, otp) {
  const result = await confirmationResult.confirm(otp);
  await reloadFirebaseUser();
  return result;
}

// ---- Link Email link (passwordless) -----------------------------------
const EMAIL_LINK_LINKING_KEY = "sp_email_for_link";

/** Send a magic link to `email`. When the visitor clicks it we return them
 * to `/me?link-email=1&…oobCode=…` and complete the LINK (not a sign-in). */
export async function sendEmailLinkForLinking(email) {
  const u = firebaseAuth.currentUser;
  if (!u) throw new Error("Not signed in");
  const cleaned = (email || "").trim().toLowerCase();
  const actionCodeSettings = {
    url: `${window.location.origin}/me?link-email=1`,
    handleCodeInApp: true,
  };
  await sendSignInLinkToEmail(firebaseAuth, cleaned, actionCodeSettings);
  try { window.localStorage.setItem(EMAIL_LINK_LINKING_KEY, cleaned); } catch { /* ignore */ }
}

export function readStoredEmailForLinking() {
  try { return window.localStorage.getItem(EMAIL_LINK_LINKING_KEY); } catch { return null; }
}

export function isEmailLinkForLinkingCallback(href = window.location.href) {
  return isSignInWithEmailLink(firebaseAuth, href);
}

/** Complete the email-link linking flow. `email` is what the user typed
 * originally (kept in localStorage). Uses `EmailAuthProvider.credentialWithLink`
 * so the credential is bound to the current signed-in user via
 * `linkWithCredential` — no re-authentication happens.
 */
export async function completeEmailLinkLinking(email, href = window.location.href) {
  const u = firebaseAuth.currentUser;
  if (!u) throw new Error("Not signed in");
  const cleaned = (email || "").trim().toLowerCase();
  const cred = EmailAuthProvider.credentialWithLink(cleaned, href);
  const result = await linkWithCredential(u, cred);
  try { window.localStorage.removeItem(EMAIL_LINK_LINKING_KEY); } catch { /* ignore */ }
  await reloadFirebaseUser();
  return result;
}

// ---- Unlink a provider -------------------------------------------------
/** Detach a provider. Refuses to unlink the ONLY remaining provider
 * (otherwise the user could lock themselves out). */
export async function unlinkProvider(providerId) {
  const u = firebaseAuth.currentUser;
  if (!u) throw new Error("Not signed in");
  const linked = u.providerData || [];
  if (linked.length <= 1) {
    const err = new Error("Cannot remove your only sign-in method — add another one first.");
    err.code = "sp/last-provider";
    throw err;
  }
  await fbUnlink(u, providerId);
  await reloadFirebaseUser();
}

// ---- Re-authenticate (Google popup) — used when linking a sensitive
// provider fails with auth/requires-recent-login. Keeps the same user.
export async function reauthenticateGoogle() {
  const u = firebaseAuth.currentUser;
  if (!u) throw new Error("Not signed in");
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: "select_account" });
  return await reauthenticateWithPopup(u, provider);
}

/** Translate a Firebase auth error code to a friendly message for our UI. */
export function accountLinkErrorMessage(err) {
  const code = err?.code || err?.message || "";
  switch (code) {
    case "auth/credential-already-in-use":
      return "That credential is already linked to another Social Pulse account. Sign in with it separately or contact support to merge accounts.";
    case "auth/email-already-in-use":
      return "That email already belongs to another account. Sign in with that account instead.";
    case "auth/provider-already-linked":
      return "This sign-in method is already linked to your account.";
    case "auth/requires-recent-login":
      return "For security, please sign in again before adding a new method.";
    case "auth/invalid-verification-code":
      return "That code was not accepted. Double-check the SMS and try again.";
    case "auth/invalid-phone-number":
      return "Please enter a valid phone number in international format (e.g. +1 555 123 4567).";
    case "auth/popup-closed-by-user":
      return "The Google window was closed before linking finished.";
    case "sp/last-provider":
      return err.message;
    default:
      return err?.message || "Could not update sign-in methods. Please try again.";
  }
}
